import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';

import { PortalAuthService } from './portal-auth.service';

/**
 * PUERTA DEL PORTAL DE FAMILIAS.
 *
 * <p>No tiene nada que ver con `authGuard` de partner-core: aquí no hay token en memoria que
 * consultar, porque la credencial es una cookie HttpOnly que el JavaScript no puede leer. La única
 * forma de saber si hay sesión es preguntárselo al backend, así que la puerta ES la llamada a
 * {@code GET /me} — y de paso deja cargados cuenta, vínculos y consentimiento para que el layout y
 * las pantallas hijas no vuelvan a pedirlos.</p>
 *
 * <p>Tres salidas: sin sesión → pantalla de ingreso; con sesión pero sin aceptar la política
 * vigente → paso 3 del ingreso (el backend responde 409 a todo lo demás, así que entrar sería
 * entrar a una pantalla rota); con sesión y consentimiento → adelante.</p>
 */
export const portalAuthGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const auth = inject(PortalAuthService);
  const router = inject(Router);

  const empresa = resolverEmpresa(route);
  if (empresa) {
    auth.empresa.set(empresa);
  }

  // Un enlace directo (p. ej. a Pagos desde un aviso de cobranza) se recuerda para volver tras ingresar.
  const destino = destinoDe(state.url, empresa);
  const ok = await auth.loadMe();
  if (!ok) {
    return router.createUrlTree([`/portal/${empresa || '_'}/acceso`], {
      queryParams: destino ? { destino } : {},
    });
  }
  if (auth.consentRequired()) {
    return router.createUrlTree([`/portal/${empresa || '_'}/acceso`], {
      queryParams: destino ? { paso: 'consentimiento', destino } : { paso: 'consentimiento' },
    });
  }
  return true;
};

/** `/portal/{empresa}/pagos?x=1` → `pagos`; nada si es la portada. */
function destinoDe(url: string, empresa: string): string | null {
  const ruta = url.split(/[?#]/)[0];
  const prefijo = `/portal/${empresa}/`;
  if (!empresa || !ruta.startsWith(prefijo)) return null;
  const resto = ruta.slice(prefijo.length);
  return resto && resto !== 'inicio' ? resto : null;
}

/** El `:empresa` puede venir en la propia ruta o en un ancestro (rutas hijas del layout). */
function resolverEmpresa(route: ActivatedRouteSnapshot): string {
  let actual: ActivatedRouteSnapshot | null = route;
  while (actual) {
    const valor = actual.paramMap.get('empresa');
    if (valor) {
      return valor;
    }
    actual = actual.parent;
  }
  return '';
}
